import React from 'react';
import PropTypes from 'prop-types';
import * as Styled from './SelectTile.styled';

/**
 * A `SelectTile` is a button used to indicate a product option.
 */
const SelectTile = ({
  isSelected,
  onChange,
  onClick,
  name,
  disabled,
  isUnavailable,
  value,
  label,
  note,
  type,
  dataLabel,
  ...rest
}) => (
  <>
    <Styled.Label
      data-testid="optionTileLabel"
      isUnavailable={isUnavailable}
      isSelected={isSelected}
      {...rest}
    >
      <Styled.Input
        checked={isSelected}
        disabled={disabled}
        name={name}
        onClick={onClick}
        onChange={onChange}
        readOnly={!onChange}
        value={value}
        type={type}
      />
      <Styled.Span isUnavailable={isUnavailable} disabled={disabled} data-label={dataLabel}>
        {label || value}
        {note && <Styled.Note>{note}</Styled.Note>}
      </Styled.Span>
    </Styled.Label>
  </>
);

SelectTile.propTypes = {
  /**
   * If `true`, the option is disabled.
   */
  disabled: PropTypes.bool,
  /**
   * If `true`, the option is selected.
   */
  isSelected: PropTypes.bool,
  /**
   * If `true`, the option displays in a less prominent state.
   */
  isUnavailable: PropTypes.bool,
  /**
   * Name attribute of the `input` element.
   */
  name: PropTypes.string,
  /**
   * The value of the option. The DOM API casts this to a string.
   */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  /**
   * The label to appear inside the option tile. If undefined, `value` prop will be used.
   */
  label: PropTypes.string,
  /**
   * Additional text to appear inside the option tile.
   */
  note: PropTypes.string,
  /**
   * Callback fired when the state is changed.
   * @param {SyntheticBaseEvent} event React `SyntheticBaseEvent`
   */
  onChange: PropTypes.func,
  /**
   * Callback fired when component is rendered as button.
   * @param {SyntheticBaseEvent} event React `SyntheticBaseEvent`
   */
  onClick: PropTypes.func,
  /**
   * Render SelectTile as a radio button or button
   */
  type: PropTypes.oneOf(['radio', 'button']),
  /**
   * The data-label attribute for the label definition
   */
  dataLabel: PropTypes.string
};

SelectTile.defaultProps = {
  disabled: false,
  isSelected: false,
  isUnavailable: false,
  label: '',
  note: '',
  name: undefined,
  onChange: undefined,
  onClick: undefined,
  type: 'radio',
  dataLabel: undefined
};

export default SelectTile;
